import React, { useCallback, useMemo } from "react";
import {
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import { Trash2, Settings } from "lucide-react";
import NodeDocsButton from "../../docs/NodeDocsButton";

// ─── Theme Color Map (full Tailwind class strings for purge safety) ─────────
export const THEME_COLORS = {
  green: {
    bgLight: "bg-green-50",
    borderDefault: "border-green-300",
    borderHover: "hover:border-green-400",
    borderSelected: "border-green-600",
    handleBg: "bg-green-600",
    handleShadow: "0 0 0 4px rgba(22, 163, 74, 0.2)",
    iconBg: "bg-green-100",
    iconText: "text-green-700",
    buttonBg: "bg-green-600",
    buttonHover: "hover:bg-green-700",
  },
  emerald: {
    bgLight: "bg-emerald-50/80",
    borderDefault: "border-emerald-300",
    borderHover: "hover:border-emerald-300",
    borderSelected: "border-emerald-500",
    handleBg: "bg-green-500",
    handleShadow: "0 0 0 4px rgba(45, 242, 68, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
    iconBg: "bg-green-50",
    iconText: "text-green-600",
    buttonBg: "bg-green-600",
    buttonHover: "hover:bg-green-700",
  },
  amber: {
    bgLight: "bg-amber-50",
    borderDefault: "border-amber-300",
    borderHover: "hover:border-amber-300",
    borderSelected: "border-amber-500",
    handleBg: "bg-amber-500",
    handleShadow: "0 0 0 4px rgba(245, 158, 11, 0.2)",
    iconBg: "bg-amber-100",
    iconText: "text-amber-700",
    buttonBg: "bg-amber-600",
    buttonHover: "hover:bg-amber-700",
  },
  blue: {
    bgLight: "bg-blue-50",
    borderDefault: "border-blue-300",
    borderHover: "hover:border-blue-400",
    borderSelected: "border-blue-500",
    handleBg: "bg-blue-500",
    handleShadow: "0 0 0 4px rgba(59, 130, 246, 0.2)",
    iconBg: "bg-blue-100",
    iconText: "text-blue-700",
    buttonBg: "bg-blue-600",
    buttonHover: "hover:bg-blue-700",
  },
  indigo: {
    bgLight: "bg-indigo-50",
    borderDefault: "border-indigo-300",
    borderHover: "hover:border-indigo-400",
    borderSelected: "border-indigo-500",
    handleBg: "bg-indigo-500",
    handleShadow: "0 0 0 4px rgba(99, 102, 241, 0.2)",
    iconBg: "bg-indigo-100",
    iconText: "text-indigo-700",
    buttonBg: "bg-indigo-600",
    buttonHover: "hover:bg-indigo-700",
  },
  sky: {
    bgLight: "bg-sky-50",
    borderDefault: "border-sky-300",
    borderHover: "hover:border-sky-400",
    borderSelected: "border-sky-500",
    handleBg: "bg-sky-500",
    handleShadow: "0 0 0 4px rgba(14, 165, 233, 0.2)",
    iconBg: "bg-sky-100",
    iconText: "text-sky-700",
    buttonBg: "bg-sky-600",
    buttonHover: "hover:bg-sky-700",
  },
  purple: {
    bgLight: "bg-purple-50",
    borderDefault: "border-purple-300",
    borderHover: "hover:border-purple-400",
    borderSelected: "border-purple-500",
    handleBg: "bg-purple-500",
    handleShadow: "0 0 0 4px rgba(168, 85, 247, 0.2)",
    iconBg: "bg-purple-100",
    iconText: "text-purple-700",
    buttonBg: "bg-purple-600",
    buttonHover: "hover:bg-purple-700",
  },
  rose: {
    bgLight: "bg-rose-50",
    borderDefault: "border-rose-300",
    borderHover: "hover:border-rose-400",
    borderSelected: "border-rose-500",
    handleBg: "bg-rose-500",
    handleShadow: "0 0 0 4px rgba(244, 63, 94, 0.2)",
    iconBg: "bg-rose-100",
    iconText: "text-rose-700",
    buttonBg: "bg-rose-600",
    buttonHover: "hover:bg-rose-700",
  },
  red: {
    bgLight: "bg-red-50",
    borderDefault: "border-red-300",
    borderHover: "hover:border-red-400",
    borderSelected: "border-red-500",
    handleBg: "bg-red-500",
    handleShadow: "0 0 0 4px rgba(239, 68, 68, 0.2)",
    iconBg: "bg-red-100",
    iconText: "text-red-700",
    buttonBg: "bg-red-600",
    buttonHover: "hover:bg-red-700",
  },
} as const;

export type ThemeColor = keyof typeof THEME_COLORS;

// ─── Props ──────────────────────────────────────────────────────────────────
export interface WorkflowBaseNodeProps {
  id: string;
  data: any;
  selected?: boolean;
  title: string;
  icon: React.ReactNode;
  nodeDocsType: string;
  themeColor: ThemeColor;
  configureLabel: string;
  statusText?: {
    configured?: string;
    notConfigured?: string;
    running?: string;
    success?: string;
    error?: string;
  };
  isConfigured?: boolean;
  validationErrors?: string[];
  hasSourceHandle?: boolean;
  hasErrorHandle?: boolean;
  onConfigure: () => void;
  children?: React.ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────────────
export const WorkflowBaseNode: React.FC<WorkflowBaseNodeProps> = ({
  id,
  data,
  selected,
  title,
  icon,
  nodeDocsType,
  themeColor,
  configureLabel,
  statusText = {},
  isConfigured = false,
  validationErrors = [],
  hasSourceHandle = false,
  hasErrorHandle = false,
  onConfigure,
  children,
}) => {
  const { deleteElements } = useReactFlow();
  const theme = THEME_COLORS[themeColor];

  // ── Execution status ────────────────────────────────────────────────
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const hasValidationErrors = validationErrors.length > 0;

  // ── Status config ───────────────────────────────────────────────────
  const statusConfig = useMemo(() => {
    if (isExecuting)
      return {
        bg: "bg-blue-500",
        text: statusText.running || "Executing...",
        pulse: true,
      };
    if (executionSuccess)
      return {
        bg: "bg-green-500",
        text: statusText.success || "Executed",
        pulse: false,
      };
    if (executionError)
      return {
        bg: "bg-red-500",
        text: statusText.error || "Failed",
        pulse: false,
      };
    return isConfigured
      ? {
          bg: "bg-green-600",
          text: statusText.configured || "Configured",
          pulse: false,
        }
      : {
          bg: "bg-gray-400",
          text: statusText.notConfigured || "Not Configured",
          pulse: false,
        };
  }, [isExecuting, executionSuccess, executionError, isConfigured, statusText]);

  // ── Handlers ────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      deleteElements({ nodes: [{ id }] });
    },
    [deleteElements, id],
  );

  const handleConfigure = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onConfigure();
    },
    [onConfigure],
  );

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  // ── Border class ────────────────────────────────────────────────────
  const borderClass = hasValidationErrors
    ? "border-red-500 shadow-red-200"
    : isExecuting
      ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
      : executionSuccess
        ? "border-green-500 shadow-md shadow-green-200"
        : executionError
          ? "border-red-500 shadow-md shadow-red-200"
          : selected
            ? `${theme.borderSelected} shadow-md`
            : `${theme.borderDefault} ${theme.borderHover}`;

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      className={`relative group ${theme.bgLight} border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${borderClass}`}
    >
      {/* NOTE: the spinner overlay + success/error badges are rendered by the
          shared withNodeExecutionStatus HOC (so every node type gets them) —
          this card only keeps the colored border glow below. */}

      {/* Target handle (left) */}
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 ${theme.handleBg} border-2 border-white`}
        style={{ boxShadow: theme.handleShadow }}
      />

      {/* Header */}
      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-lg shadow-sm ${theme.iconBg}`}>
            {icon}
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">{title}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType={nodeDocsType} />
          <button
            onClick={handleDelete}
            className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Configure button */}
      <div className="mb-3" onClick={stopPropagation}>
        <button
          onClick={handleConfigure}
          className={`w-full py-2.5 rounded-md text-white text-xs font-medium flex justify-center gap-2 shadow-sm transition-colors ${theme.buttonBg} ${theme.buttonHover}`}
        >
          <Settings className="w-3.5 h-3.5" /> {configureLabel}
        </button>
      </div>

      {/* Node-specific inline content */}
      {children}

      {/* Source handle (right) */}
      {hasSourceHandle && (
        <Handle
          type="source"
          position={Position.Right}
          className={`w-3 h-3 ${theme.handleBg} border-2 border-white`}
          style={{ boxShadow: theme.handleShadow }}
        />
      )}

      {/* Error handle (bottom) - diamond shape */}
      {hasErrorHandle && (
        <>
          <Handle
            type="source"
            id="error"
            position={Position.Bottom}
            className="!w-[10px] !h-[10px] !bg-red-500 !border !border-white !rounded-[2px] !rotate-45"
            style={{ bottom: "-6px" }}
          />
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-[18px] pointer-events-none z-10">
            <span className="text-[7px] font-medium text-red-400 lowercase tracking-wide">error</span>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkflowBaseNode;
